/**
 * Экран «Продажи» — §14.2, метрики `R2`–`R5`, `R7`, `R8` с раскрытием.
 *
 * Четыре разреза одной выручки: по категориям, по часам, по официантам,
 * по способам оплаты. Плюс рейтинг блюд — и он единственный, у кого есть
 * переключатель.
 *
 * **Рейтинг блюд переключается, а не выбирается за человека.** §9.2:
 * «блюдо может быть первым по количеству и последним по вкладу в маржу.
 * Именно поэтому рейтинги показываются раздельно, а не одним топом».
 * Рейтинга «вообще» не существует, и экран, показывающий один под именем
 * другого, даёт неверный ответ на заданный вопрос.
 *
 * **Каждый столбик раскрывается до чеков** (§14.3): нажатие на категорию
 * или блюдо открывает список чеков, в которые они вошли, с тем же
 * периодом. Это и есть та цепочка, ради которой §14.3 написан, — и
 * работает она потому, что фильтры списка чеков совпадают с разрезами
 * метрик, а отбор — с отбором выручки.
 */

import { useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { Button } from '@shell/ui';
import { Loading } from '@shell/states';
import { financeApi } from '../api';
import type { MetricView, PeriodParams } from '../api';
import { Breakdown } from '../charts/Charts';
import type { BreakdownRow } from '../charts/Charts';
import {
  DownloadLink,
  ExplanationModal,
  FreshnessBadge,
  PeriodPicker,
  ScreenError,
  Section,
  useExplanation,
} from '../components';

/**
 * Разрезы экрана. Порядок — от самого частого вопроса к самому редкому.
 *
 * `value` — ключ суммы в строке метрики, из `metrics/revenue.py`
 * дословно. Он разный: часы и официанты отдают `revenue`, категории и
 * оплаты — `amount`. До 11.09.2026 ключ был один на все, и «По часам» и
 * «По официантам» рисовали пустые оси.
 */
const BREAKDOWNS = [
  { code: 'revenue.by_category', title: 'По категориям', horizontal: true, filter: 'category', value: 'amount' },
  { code: 'revenue.by_hour', title: 'По часам', horizontal: false, filter: null, value: 'revenue' },
  { code: 'revenue.by_waiter', title: 'По официантам', horizontal: true, filter: 'waiter', value: 'revenue' },
  { code: 'revenue.by_payment', title: 'По способам оплаты', horizontal: true, filter: null, value: 'amount' },
] as const;

export function Sales({
  period,
  onPeriodChange,
  onDrill,
}: {
  period: PeriodParams;
  onPeriodChange: (next: PeriodParams) => void;
  onDrill: (filters: { category?: string; item?: string; waiter?: string }) => void;
}) {
  const [rankBy, setRankBy] = useState<'revenue' | 'quantity'>('revenue');
  const [explained, explain, closeExplanation] = useExplanation();

  const codes = [...BREAKDOWNS.map((item) => item.code), 'sales.top_items'];
  const queries = useQueries({
    queries: codes.map((code) => ({
      queryKey: ['finance', 'metric', code, period],
      queryFn: () => financeApi.metric(code, period),
    })),
  });

  if (queries.some((query) => query.isLoading)) return <Loading />;
  const failed = queries.find((query) => query.error);
  if (failed) {
    return (
      <ScreenError
        error={failed.error}
        period={period}
        anchor={period.to ?? null}
        onPeriodChange={onPeriodChange}
        onRetry={() => queries.forEach((query) => query.refetch())}
      />
    );
  }

  const results = queries.map((query) => query.data as MetricView);
  const top = results[results.length - 1];
  const freshness = results[0]?.freshness;

  /**
   * Код измерения из строки разреза — то, чем фильтруется список чеков.
   *
   * Ключей несколько, и они разные у разных метрик: `category_code` у
   * категорий, `waiter_rk7_code` у официантов, `code` у блюд. Имена взяты
   * из `metrics/revenue.py` дословно; выдумывать здесь общий ключ значило
   * бы переименовывать поля движка в браузере, а потом искать, почему
   * раскрытие «работает, но не фильтрует».
   */
  const codeOf = (row: BreakdownRow): string | undefined => {
    const value = row.code ?? row.category_code ?? row.waiter_rk7_code;
    return typeof value === 'string' ? value : undefined;
  };

  return (
    <div className="screen">
      {freshness && <FreshnessBadge freshness={freshness} />}
      <PeriodPicker period={period} anchor={period.to ?? null} onChange={onPeriodChange} />

      <div className="grid-2">
        {BREAKDOWNS.map((breakdown, index) => {
          const metric = results[index];
          return (
            <Section
              key={breakdown.code}
              title={breakdown.title}
              actions={
                <>
                  <Button small variant="quiet" onClick={() => explain(metric)}>
                    Как посчитано
                  </Button>
                  <DownloadLink href={financeApi.csvUrl(breakdown.code, period)}>
                    CSV
                  </DownloadLink>
                </>
              }
            >
              <Breakdown
                title={metric.name}
                rows={metric.rows as BreakdownRow[]}
                valueKey={breakdown.value}
                horizontal={breakdown.horizontal}
                onBarClick={
                  breakdown.filter
                    ? (row) => {
                        const value = codeOf(row);
                        if (value) onDrill({ [breakdown.filter]: value });
                      }
                    : undefined
                }
              />
            </Section>
          );
        })}
      </div>

      <Section
        title="Блюда"
        actions={
          <>
            {/* Два рейтинга рядом, а не один «топ»: §9.2. Активный
                выделен — человек должен видеть, какой из них перед ним,
                не нажимая. */}
            <Button
              small
              variant={rankBy === 'revenue' ? 'primary' : 'quiet'}
              onClick={() => setRankBy('revenue')}
            >
              По выручке
            </Button>
            <Button
              small
              variant={rankBy === 'quantity' ? 'primary' : 'quiet'}
              onClick={() => setRankBy('quantity')}
            >
              По количеству
            </Button>
            <DownloadLink href={financeApi.csvUrl('sales.top_items', period)}>CSV</DownloadLink>
          </>
        }
      >
        <Breakdown
          title={rankBy === 'revenue' ? 'По выручке' : 'По количеству'}
          rows={top.rows as BreakdownRow[]}
          valueKey={rankBy === 'revenue' ? 'amount' : 'quantity'}
          unit={rankBy === 'revenue' ? '₽' : 'шт'}
          horizontal
          onBarClick={(row) => {
            const value = codeOf(row);
            if (value) onDrill({ item: value });
          }}
        />
        {typeof top.extras.never_sold === 'number' && top.extras.never_sold > 0 && (
          <p className="faint">
            Не продавалось ни разу за период: {String(top.extras.never_sold)} позиций меню.
          </p>
        )}
        <p className="faint chart-hint">
          Нажмите на столбик, чтобы увидеть чеки, в которые он вошёл.
        </p>
      </Section>

      <ExplanationModal metric={explained} onClose={closeExplanation} />
    </div>
  );
}
